import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { bankService, homeService } from '../clients/ApiClients';
import { CounterpartyBankAccountReturn } from '../clients/rust-api-client';
import { apiErrorMessage } from '../utils/apiErrors';
import { useUser } from '../hooks/UserContext';
import { canManagePayees } from '../constants/UserTypes';

interface Props {
  memberId: string;
}

/**
 * The bank account a homeowner has on file for paying by ACH, as staff
 * see it: masked, with the authorisation they accepted and when. Staff
 * can remove it (Owner/Admin); only the homeowner can add one, because
 * the authorisation is theirs to give.
 */
const MemberBankAccountCard: React.FC<Props> = ({ memberId }) => {
  const { userType } = useUser();
  const mayRemove = canManagePayees(userType);
  // The route is HOA-scoped; a member's account is keyed to the HOA of
  // their first home.
  const [hoaId, setHoaId] = useState<string | null>(null);
  const [account, setAccount] = useState<CounterpartyBankAccountReturn | null>(
    null
  );
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      let hoa = hoaId;
      if (!hoa) {
        const homes = await homeService.getHomesByMember({ memberId });
        const list = Array.isArray(homes.data)
          ? homes.data
          : ((homes.data as { homes?: Array<{ hoa_id: string }> }).homes ?? []);
        hoa = list[0]?.hoa_id ?? null;
        setHoaId(hoa);
        if (!hoa) {
          setAccount(null);
          setLoaded(true);
          return;
        }
      }
      const resp = await bankService.getMemberBankAccount({
        hoaId: hoa,
        memberId,
      });
      setAccount(resp.data);
      setError(null);
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        setAccount(null);
        setError(null);
      } else {
        setError(apiErrorMessage(e, "Couldn't load the bank account on file."));
      }
    } finally {
      setLoaded(true);
    }
  }, [hoaId, memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (): Promise<void> => {
    if (!hoaId) return;
    try {
      setRemoving(true);
      await bankService.retireMemberBankAccount({ hoaId, memberId });
      setAccount(null);
      setConfirm(false);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't remove the bank account."));
      setConfirm(false);
    } finally {
      setRemoving(false);
    }
  };

  if (!loaded) return null;

  return (
    <section className="py-3">
      <h2 className="text-xs font-medium uppercase tracking-wide text-fg-muted">
        Bank account for dues
      </h2>
      <ErrorAlert className="mt-2" onRetry={() => void load()}>
        {error}
      </ErrorAlert>
      {account ? (
        <div className="mt-2 text-sm text-fg">
          <p>{account.ach_name}</p>
          <p className="text-fg-muted">
            {account.account_type} ending in {account.account_last4}
          </p>
          {account.authorized_at && (
            <p className="text-fg-muted">
              Authorized {new Date(account.authorized_at).toLocaleDateString()}
              {account.authorization_text_version
                ? ` (${account.authorization_text_version})`
                : ''}
            </p>
          )}
          {mayRemove && (
            <Button plain className="mt-1" onClick={() => setConfirm(true)}>
              Remove
            </Button>
          )}
        </div>
      ) : (
        !error && (
          <p className="mt-2 text-sm text-fg-muted">
            None on file. The homeowner adds one in the member portal.
          </p>
        )
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Remove this bank account?</DialogTitle>
        <DialogDescription>
          The homeowner will have to add it again, and accept the authorization
          again, to pay by bank account. Payments already submitted are not
          affected.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setConfirm(false)} disabled={removing}>
            Keep it
          </Button>
          <Button color="red" onClick={remove} disabled={removing}>
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default MemberBankAccountCard;
